import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import * as _ from 'lodash';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { ArticleFamilyDto } from 'src/app/shared/dto/article-family.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { ArticleFamilyService } from '../../services/article-family.service';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective, NzInputModule } from 'ng-zorro-antd/input';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { NzIconDirective, NzIconModule } from 'ng-zorro-antd/icon';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-article-families',
    standalone: true,
    imports: [
        CommonModule,
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        NzInputModule,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzFormModule,
        NzIconDirective,
        NzIconModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './article-families.component.html',
    styleUrl: './article-families.component.scss'
})
export class ArticleFamiliesComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    currentUser: UserDto = new UserDto();
    familySelected: ArticleFamilyDto = new ArticleFamilyDto();

    parentFamilySelected: number | null = null;

    families: ArticleFamilyDto[] = [];

    searchText: string = '';
    filteredFamilies: ArticleFamilyDto[] = [];

    availableParents: ArticleFamilyDto[] = [];

    isLoading: boolean = true;
    isSaving: boolean = false;

    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        name: FormControl<string>;
    }>;

    private unsubscribe$ = new Subject<void>();
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly familiesService = inject(ArticleFamilyService);
    private readonly message = inject(NzMessageService);
    private readonly fb = inject(NonNullableFormBuilder);

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    constructor() {
        super('articleFamilies', '');
        this.validateForm = this.fb.group({
            name: ['', [Validators.required]]
        });
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.familiesService.setupWebSocket();
        this.familiesService.articleFamilies$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (families: ArticleFamilyDto[]) => {
                    this.families = families;

                    const updatedFamilySelected  = this.families.find(f => f.id === this.familySelected.id);

                    if (updatedFamilySelected ) {
                        this.familySelected = updatedFamilySelected ;
                        this.setParentFromData();
                    } else if (this.drawerMode !== DrawerMode.CREATE && this.drawerVisible) {
                        this.message.warning('La familia que estaba viendo/editando fue eliminada.');
                        this.closeDrawer();
                    }

                    this.updateAvailableParents();

                    this.filterFamilies();
                },
                error: () => {
                    this.message.error('Error cargando familias de artículos');
                }
            });
        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.familiesService.discontectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    filterFamilies(): void {
        if (!this.searchText) {
            this.filteredFamilies = [...this.families];
        } else {
            const term = this.searchText.toLowerCase();
            this.filteredFamilies = this.families.filter(family =>
                family.name.toLowerCase().includes(term) ||
                family.id.toString().includes(term)
            );
        }
    }

    create() {
        this.familySelected = { id: 0, name: '', parentArticleFamilyId: -1 };
        this.parentFamilySelected = null;
        this.updateAvailableParents();

        this.validateForm.enable();
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nueva familia';
        this.fieldsReadOnly = false;
        this.openDrawer();
    }

    view(data: ArticleFamilyDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.familySelected = data;

        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo familia ' + this.familySelected?.name;
        this.fieldsReadOnly = true;

        this.setParentFromData();
        this.updateAvailableParents();

        this.openDrawer();
    }

    edit(data: ArticleFamilyDto) {
        this.validateForm.patchValue(data);
        this.validateForm.enable();
        this.familySelected = data;

        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando familia ' + this.familySelected?.name;
        this.fieldsReadOnly = false;

        this.setParentFromData();
        this.updateAvailableParents();

        this.openDrawer();
    }

    private setParentFromData(): void {
    this.parentFamilySelected = this.familySelected.parentArticleFamilyId 
        ? this.familySelected.parentArticleFamilyId 
        : null;
}


    private updateAvailableParents() {
        this.availableParents = this.families.filter(f => f.id !== this.familySelected.id);
    }

    cancelEditing() {
        this.closeDrawer();
    }

    async delete(familyId: number) {
        try {
            if (!window.confirm('¿Estás seguro de que quieres eliminar esta familia? Esta acción no se puede deshacer.')) {
                return;
            }

            const deletedFamily: ArticleFamilyDto = await this.familiesService.deleteFamily(familyId);
            const index = this.families.findIndex((p) => p.id === familyId);

            if (index < 0) {
                this.message.create('error', `No se ha podido eliminar la familia ${deletedFamily.name} con id ${familyId}, familia no encontrada`);
                return;
            }

            this.families.splice(index, 1);
            this.families = _.cloneDeep(this.families);

            this.filterFamilies();

            this.message.create('success', 'Familia eliminada correctamente');
        } catch (e: any) {
            this.message.create('error', `No se ha podido eliminar la familia ${familyId}. Error: ${e.message}`);
        }
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.parentFamilySelected = null;
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    async submitForm() {
        try {
            this.isSaving = true;

            if (!this.validateForm.valid) {
                this.isSaving = false;
                this.message.error(`Porfavor, rellene los campos obligatorios`);
                return;
            }

            const familyToBeProcessed: ArticleFamilyDto = {
                id: this.familySelected.id,
                name: this.validateForm.controls.name.value ? this.validateForm.controls.name.value : '',
                parentArticleFamilyId: this.parentFamilySelected ? this.parentFamilySelected : null
            };


            if (this.drawerMode === DrawerMode.CREATE) {
                const createdFamily: ArticleFamilyDto = await this.familiesService.createFamily(familyToBeProcessed);
                this.families.push(createdFamily);
                this.families = _.cloneDeep(this.families);

                this.filterFamilies();

                this.isSaving = false;
                this.message.create('success', 'Familia creada correctamente');

            } else if (this.drawerMode === DrawerMode.EDIT) {
                const updatedFamily: ArticleFamilyDto = await this.familiesService.updateFamily(familyToBeProcessed);
                const index = this.families.findIndex((r) => r.id === updatedFamily.id);

                if (index < 0) {
                    this.isSaving = false;
                    this.message.create('error', `No se ha podido actualizar la familia ${familyToBeProcessed.name} con id ${familyToBeProcessed.id}, familia no encontrada`);
                    return;
                }

                this.families[index] = updatedFamily;
                this.families = _.cloneDeep(this.families);

                this.filterFamilies();

                this.isSaving = false;
                this.message.create('success', 'Familia actualizada correctamente');
            }

            this.closeDrawer();
        } catch (e: any) {
            this.isSaving = false;
            this.message.create('error', `Error: ${e.message}`);
        }
    }
}